import { useState, useMemo, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth.js';
import { useTimesheet } from '../hooks/useTimesheet.js';
import { weekRangeOffset, rangeKey, centsToUsd } from '../lib/format.js';
import { get } from '../api/client.js';
import TimesheetTable from '../components/timesheet/TimesheetTable.jsx';

export default function TimesheetPage() {
  const { user, isAdmin } = useAuth();
  const [range, setRange] = useState(() => weekRangeOffset(0));
  const [target, setTarget] = useState(user?.discord_id);
  const [users, setUsers] = useState([]);

  const { data } = useTimesheet({ from: range.from, to: range.to, target });

  // For admin's "switch crew" dropdown, build a list of users seen in `all` view.
  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    get(`/api/timesheet?from=${range.from}&to=${range.to}&user=all`).then(b => {
      if (!alive) return;
      const seen = new Map();
      for (const s of (b.summary || [])) seen.set(s.user_id, s.user_name);
      for (const e of (b.entries || [])) seen.set(e.user_id, e.user_name);
      setUsers(Array.from(seen.entries()).map(([id, name]) => ({ id, name })));
    }).catch(() => {});
    return () => { alive = false; };
  }, [isAdmin, range]);

  const downloadCsv = () => {
    const u = encodeURIComponent(target);
    window.location.href = `/api/export?from=${range.from}&to=${range.to}&user=${u}`;
  };

  const myTotal = useMemo(() => {
    if (!data?.summary?.length) return null;
    return data.summary.find(s => s.user_id === (target === 'all' ? null : target))
      || data.summary[0];
  }, [data, target]);

  return (
    <div className="panel">
      <h3 className="panel-title">
        <span><i className="ph ph-table" /> Timesheet</span>
        <button className="btn-sm" onClick={downloadCsv}>
          <i className="ph ph-download-simple" /> CSV
        </button>
      </h3>

      <div className="range-bar">
        <select className="field-select"
          value={rangeKey(range)}
          onChange={e => setRange(weekRangeOffset(Number(e.target.value)))}>
          <option value="0">This week (Mon–Sun)</option>
          <option value="-1">Last week</option>
          <option value="-2">2 weeks ago</option>
          <option value="-3">3 weeks ago</option>
        </select>
        {isAdmin && (
          <select className="field-select"
            value={target}
            onChange={e => setTarget(e.target.value)}>
            <option value={user.discord_id}>{user.display_name} (me)</option>
            <option value="all">All crew</option>
            {users.filter(u => u.id !== user.discord_id).map(u => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        )}
      </div>

      {target !== 'all' && myTotal && (
        <div className="sum-row">
          <div className="sum-card">
            <div className="sum-card-label">Total hours</div>
            <div className="sum-card-value">{(myTotal.total_hrs ?? 0).toFixed(2)}</div>
          </div>
          <div className="sum-card">
            <div className="sum-card-label">Regular</div>
            <div className="sum-card-value">{(myTotal.regular_hrs ?? 0).toFixed(2)}</div>
            <div className="sum-card-sub">{centsToUsd(myTotal.regular_pay_cents)}</div>
          </div>
          <div className="sum-card">
            <div className="sum-card-label">Overtime</div>
            <div className="sum-card-value orange">{(myTotal.ot_hrs ?? 0).toFixed(2)}</div>
            <div className="sum-card-sub">{centsToUsd(myTotal.ot_pay_cents)}</div>
          </div>
          <div className="sum-card">
            <div className="sum-card-label">Total pay</div>
            <div className="sum-card-value orange">
              {centsToUsd(myTotal.total_pay_cents)}
            </div>
          </div>
        </div>
      )}

      {target === 'all' && data?.summary && (
        <table className="tbl" style={{ marginBottom: 20 }}>
          <thead>
            <tr>
              <th>Crew</th>
              <th className="num">Hrs</th>
              <th className="num">Reg</th>
              <th className="num">OT</th>
              <th className="num">Pay</th>
            </tr>
          </thead>
          <tbody>
            {data.summary.map(s => (
              <tr key={s.user_id}>
                <td>{s.user_name}</td>
                <td className="num">{(s.total_hrs ?? 0).toFixed(2)}</td>
                <td className="num">{(s.regular_hrs ?? 0).toFixed(2)}</td>
                <td className="num">{(s.ot_hrs ?? 0).toFixed(2)}</td>
                <td className="num">{centsToUsd(s.total_pay_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <TimesheetTable
        entries={data?.entries}
        showCrew={target === 'all' || isAdmin}
        isAdmin={isAdmin}
      />
    </div>
  );
}
